import {create} from 'zustand';
import * as THREE from 'three';
import {devtools, subscribeWithSelector} from 'zustand/middleware';
import { computeRegionFromPlane } from '../components/tools/helpers';
import useEditorStore from './useEditorStore';
import { geometryWorkerPool } from '../utils/WorkerPool.js';

const usePreparingScanStore = create(
  devtools(
    subscribeWithSelector((set, get) => ({
        selectionPoints: /**@type {Vector3[]} */ [],
        selectedRegion: /**@type {Set<number>} */ (new Set()),
        isDraggingPoint: false,

        selectedFillVertices: (new Set()),
        setSelectedFillVertices: (selectedFillVertices) => set({selectedFillVertices: selectedFillVertices}),

        addPoint: point => {
            const pts = [...get().selectionPoints, point];
            set({selectionPoints: pts});

            // Compute the selected region if we have two points
            if (pts.length === 2) {
                const sceneRefs = useEditorStore.getState().sceneRefs;
                const region = computeRegionFromPlane(pts[0], pts[1], sceneRefs);
                set({ selectedRegion: region });
            }
        },

        updatePoint: (index, newPoint) => {
            const pts = [...get().selectionPoints];
            if (index >= 0 && index < pts.length) {
                pts[index] = newPoint;
                set({selectionPoints: pts});

                // Only recompute if not dragging (will compute on drag end)
                if (pts.length === 2 && !get().isDraggingPoint) {
                    const sceneRefs = useEditorStore.getState().sceneRefs;
                    const region = computeRegionFromPlane(pts[0], pts[1], sceneRefs);
                    set({ selectedRegion: region });
                }
            }
        },

        setIsDraggingPoint: (isDragging) => {
            set({isDraggingPoint: isDragging});
        },

        clearSelection: () => {
            set({selectionPoints: [], selectedRegion: new Set(), isDraggingPoint: false});
        },

        createFillCommand: () => {
          const { selectedFillVertices, clearFillSelection } = usePreparingScanStore.getState();
          const { sceneRefs, replaceShadedMesh, setIsLoading } = useEditorStore.getState();
          const originalMesh = sceneRefs.shadedMesh;
      
          if (selectedFillVertices.size === 0) return null;
      
          // Capture the state needed for the undo function
          const originalGeometry = originalMesh.geometry;

          const geom          = originalMesh.geometry
          const posArray      = geom.attributes.position.array      // Float32Array
          const idxArray      = geom.index.array                    // TypedArray
          const originalColors= geom.userData.originalColors        // Float32Array

          // make copies so we don’t detach the originals
          const posCopy   = new Float32Array(posArray)
          const idxCopy   = new (idxArray.constructor)(idxArray)
          const colCopy   = new Float32Array(originalColors)
      
          return {
              execute: async () => {
                  setIsLoading(true, 'Filling holes in mesh...');
                  try{
                    // Use universal worker pool instead of creating dedicated worker
                    const result = await geometryWorkerPool.execute(
                      'fillHole',
                      posCopy,
                      idxCopy,
                      colCopy,
                      Array.from(selectedFillVertices),
                      { maxEdgeLength: 0.1, gridSpacing: 0.15 }
                    );

                    const newGeom = new THREE.BufferGeometry()
                    newGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(result.positions), 3))
                    newGeom.setIndex(new THREE.BufferAttribute(new Uint32Array(result.indices), 1))
                    newGeom.setAttribute("color", new THREE.Float32BufferAttribute(result.colors, 3));
                    newGeom.userData.originalColors = new Float32Array(result.colors)
                    const newMesh = new THREE.Mesh(newGeom, originalMesh.material)
                    newMesh.matrix.copy(originalMesh.matrix)
                    replaceShadedMesh(newMesh)
                    clearFillSelection();
                  } catch(error){
                    console.error(`Couldn't fill the hole. ${error}`);
                  } finally{
                    setIsLoading(false);
                  }

              },
              undo: () => {
                  const restoredMesh = new THREE.Mesh(originalGeometry, originalMesh.material);
                  restoredMesh.matrix.copy(originalMesh.matrix);
                  replaceShadedMesh(restoredMesh);
              }
          };
        }
,

        clearFillSelection: () => {
            set({selectedFillVertices: new Set()});
        },
    }))
  )
);

export default usePreparingScanStore;
